import { useMemo } from 'react';
import type { TimesheetEntry } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import type { DayName } from '../timesheetGridUtils';

export interface UseTimesheetValidationParams {
  entries: TimesheetEntry[];
  visibleDays: DayName[];
  organizationSettings: OrganisationTimesheetSettings | undefined;
  isDaily: boolean;
  /** True when the active company posts costing transactions to a financial backend. */
  companyHasCostingIntegration: boolean;
}

export interface UseTimesheetValidationResult {
  hasMissingDescriptions: boolean;
  hasMissingProjects: boolean;
  hasMissingRoles: boolean;
  hasMissingCostCentres: boolean;
  /** Count of entries with empty descriptions, for the bulk fill feature. */
  emptyDescriptionCount: number;
}

/** Submission validation flags derived from the current entries and org settings. */
export function useTimesheetValidation({
  entries,
  visibleDays,
  organizationSettings,
  isDaily,
  companyHasCostingIntegration,
}: UseTimesheetValidationParams): UseTimesheetValidationResult {
  // Check for missing descriptions (used for submission validation)
  // Only consider descriptions missing if they are required by organization settings
  const hasMissingDescriptions = useMemo(() => {
    if (organizationSettings?.requireDescription !== 'always') {
      return false; // Descriptions not required, so never consider them missing
    }
    if (isDaily) {
      // Daily mode — every day with hours > 0 must have a note
      return entries.some(entry =>
        !entry.isReadOnly &&
        visibleDays.some(day => (entry.hours[day] ?? 0) > 0 && !entry.dailyNotes?.[day]?.trim())
      );
    }
    // Row mode — every entry must have a description
    return entries.some(entry => !entry.isReadOnly && !entry.description.trim());
  }, [entries, organizationSettings?.requireDescription, isDaily, visibleDays]);

  // Check for missing projects (always required — a timesheet row without a project is meaningless)
  const hasMissingProjects = useMemo(() => {
    return entries.some(entry => !entry.isReadOnly && !entry.projectId);
  }, [entries]);

  // Check for missing roles (used for submission validation)
  // Only consider roles missing if showRole AND requireRole are both enabled
  const hasMissingRoles = useMemo(() => {
    if (!organizationSettings?.showRole || !organizationSettings?.requireRole) {
      return false;
    }
    return entries.some(entry => !entry.roleId);
  }, [entries, organizationSettings?.showRole, organizationSettings?.requireRole]);

  // Check for missing cost centres (used for submit/resubmit validation).
  // A cost centre is required when either the org setting requires it, or the
  // current company posts costing transactions to a financial backend
  // (Access Financials / Dimensions) — in which case submitting without one would
  // fail at the posting step. Only enforced when the field is shown. A cost centre
  // only applies once a project is selected, and read-only rows (absences,
  // scheduled bookings) carry their own defaults, so ignore both.
  const costCentreRequired =
    organizationSettings?.showCostCentre !== false &&
    (organizationSettings?.requireCostCentre === true || companyHasCostingIntegration);
  const hasMissingCostCentres = useMemo(() => {
    if (!costCentreRequired) {
      return false;
    }
    return entries.some(entry => !entry.isReadOnly && entry.projectId && !entry.costCentreId);
  }, [entries, costCentreRequired]);

  // Count entries with empty descriptions for bulk fill feature
  const emptyDescriptionCount = useMemo(() => {
    if (isDaily) return 0;
    return entries.filter(entry => !entry.description.trim()).length;
  }, [entries, isDaily]);

  return {
    hasMissingDescriptions,
    hasMissingProjects,
    hasMissingRoles,
    hasMissingCostCentres,
    emptyDescriptionCount,
  };
}
